import { useMemo } from 'react';
import { enumEntries } from '../../../routes/types/utilityTypes';
import { ICustomQuestionTypeConfiguration } from 'survey-core';
import { Species } from '../api/types/digitalForms.api.types';
import { PatientSex } from '../api/types/patient.types';

export const usePatientFormQuestions = ({ species }: { species: Species[] }) => {
  const speciesChoices = useMemo(() => {
    if (!species) return [];

    // Prepare species choices from database data (same logic as patient forms)
    const suggestedSpeciesIds = [1, 2]; // Assuming 1 = Canine, 2 = Feline
    const notDisplayedSpeciesIds = [0, 999]; // Assuming 0 = Any, 999 = Unknown

    const sortedSpecies = species
      .filter((specie) => !notDisplayedSpeciesIds.includes(specie.id))
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((specie) => ({
        value: specie.id.toString(),
        text: specie.value,
      }));

    return [
      ...sortedSpecies.filter((item) => suggestedSpeciesIds.includes(Number(item.value))),
      ...sortedSpecies.filter((item) => !suggestedSpeciesIds.includes(Number(item.value))),
    ];
  }, [species]);

  // Patient question types including patient_species with dynamic choices
  const patientQuestions: ICustomQuestionTypeConfiguration[] = useMemo(
    () => [
      {
        name: 'patient_pet_name',
        title: 'Patient Pet Name',
        defaultQuestionTitle: 'Patient Pet Name',
        questionJSON: {
          type: 'text',
          placeholder: "Enter pet's name...",
          isRequired: true,
          maxLength: 50,
        },
        inheritBaseProps: true,
      },
      {
        name: 'patient_birthdate',
        title: 'Patient Birthdate',
        defaultQuestionTitle: 'Patient Birthdate',
        questionJSON: {
          type: 'text',
          inputType: 'date',
          placeholder: 'Select birthdate...',
        },
        inheritBaseProps: true,
      },
      {
        name: 'patient_sex',
        title: 'Patient Sex',
        defaultQuestionTitle: 'Patient Sex',
        questionJSON: {
          type: 'dropdown',
          placeholder: 'Select sex...',
          isRequired: true,
          choices: enumEntries(PatientSex).map(([label, id]) => ({
            value: id.toString(),
            text: label,
          })),
        },
        inheritBaseProps: true,
      },
      {
        name: 'patient_breed',
        title: 'Patient Breed',
        defaultQuestionTitle: 'Patient Breed',
        questionJSON: {
          type: 'text',
          placeholder: 'Enter breed...',
          maxLength: 100,
        },
        inheritBaseProps: true,
      },
      {
        name: 'patient_species',
        title: 'Patient Species',
        defaultQuestionTitle: 'Patient Species',
        questionJSON: {
          type: 'dropdown',
          placeholder: 'Select species...',
          isRequired: true,
          choices: speciesChoices,
        },
        inheritBaseProps: true,
      },
    ],
    [speciesChoices] // Depends on speciesChoices
  );
  return {
    patientQuestions,
    isReady: !!species,
    speciesChoices,
  };
};
